import { Calendar, Users, Clock, Play, Eye, MoreVertical, Copy, Trash2, Edit, Radio, BarChart3 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  scheduled: { label: 'Planifié', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  live: { label: '● EN DIRECT', color: 'bg-red-50 text-red-600 border-red-200 animate-pulse' },
  ended: { label: 'Terminé', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  cancelled: { label: 'Annulé', color: 'bg-orange-50 text-orange-600 border-orange-200' },
};

export default function WebinarCard({ webinar, onEdit, onDelete, onDuplicate, onJoin, onStart, onAnalytics, isHost, userRole }) {
  const status = statusConfig[webinar.status] || statusConfig.draft;
  const schedule = webinar.schedule || {};
  const settings = webinar.settings || {};
  const analytics = webinar.analytics || {};
  const isLive = webinar.status === 'live';

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return iso; }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group relative rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-md ${
        isLive
          ? 'border-red-200 bg-red-50/30'
          : 'border-zinc-200 bg-white hover:border-zinc-300'
      }`}
    >
      {/* Banner */}
      <div className="relative h-36 bg-gradient-to-br from-cyan-50 via-violet-50 to-zinc-100 overflow-hidden">
        {webinar.banner_url && (
          <img src={webinar.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

        {/* Status badge */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${status.color}`}>
            {isLive && <Radio className="w-3 h-3" />}
            {status.label}
          </span>
        </div>

        {/* Actions */}
        {(isHost || userRole === 'admin') && (
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/80 backdrop-blur border border-zinc-200 text-zinc-700 hover:bg-white">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-zinc-200">
                <DropdownMenuItem onClick={() => onAnalytics?.(webinar)} className="text-zinc-700 focus:bg-zinc-100 cursor-pointer">
                  <BarChart3 className="w-4 h-4 mr-2" /> Analytiques
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit?.(webinar)} className="text-zinc-700 focus:bg-zinc-100 cursor-pointer">
                  <Edit className="w-4 h-4 mr-2" /> Modifier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicate?.(webinar)} className="text-zinc-700 focus:bg-zinc-100 cursor-pointer">
                  <Copy className="w-4 h-4 mr-2" /> Dupliquer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete?.(webinar)} className="text-red-600 focus:bg-red-50 cursor-pointer">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Pricing badge */}
        {webinar.pricing?.type === 'paid' && (
          <div className="absolute top-3 right-3 group-hover:hidden">
            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold border border-amber-500/30">
              {webinar.pricing.price} {webinar.pricing.currency || '€'}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-zinc-900 text-base truncate group-hover:text-cyan-600 transition-colors">
            {webinar.title}
          </h3>
          {webinar.description && (
            <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{webinar.description}</p>
          )}
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> {formatDate(schedule.start_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {schedule.duration_minutes || 60} min
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {analytics.total_registrations || 0}/{settings.max_participants || 100}
          </span>
        </div>

        {/* Tags */}
        {webinar.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {webinar.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200 text-[10px] font-medium">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Host */}
        <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
          <span className="text-xs text-zinc-500">
            Par <span className="text-zinc-700 font-medium">{webinar.created_by_name || 'Instructeur'}</span>
          </span>

          {/* CTA */}
          {isLive ? (
            <Button size="sm" onClick={() => onJoin?.(webinar)}
              className="bg-red-500 hover:bg-red-600 text-white text-xs h-7 gap-1.5 animate-pulse">
              <Play className="w-3 h-3" /> Rejoindre
            </Button>
          ) : webinar.status === 'scheduled' ? (
            isHost ? (
              <Button size="sm" onClick={() => onStart?.(webinar)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs h-7 gap-1.5">
                <Play className="w-3 h-3" /> Démarrer
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => onJoin?.(webinar)}
                className="text-xs h-7 gap-1.5 border-zinc-200 text-zinc-600 hover:bg-zinc-50">
                S'inscrire
              </Button>
            )
          ) : webinar.status === 'ended' ? (
            <Button size="sm" variant="ghost" onClick={() => onJoin?.(webinar)}
              className="text-xs h-7 gap-1.5 text-zinc-500 hover:text-zinc-700">
              <Eye className="w-3 h-3" /> Voir replay
            </Button>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
